import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useIntersectionObserver } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon, CloseIcon } from '#/shared/ui/icons'
import { ClearButton } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'
import { Skeleton } from '#/shared/ui/skeleton'

export interface ComboboxOption {
  value: string
  label: string
  tag?: string
  disabled?: boolean
}

export interface ComboboxAction {
  label: string
  onClick?: () => void
}

export interface ComboboxProps {
  label?: string
  labelSize?: LabelSize
  suffix?: React.ReactNode
  options: ComboboxOption[]
  action?: ComboboxAction
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  onSelect?: (option: ComboboxOption) => void
  onClear?: () => void
  placeholder?: string
  clearable?: boolean
  disabled?: boolean
  error?: boolean
  success?: boolean
  filter?: (option: ComboboxOption, query: string) => boolean
  emptyMessage?: React.ReactNode
  loading?: boolean
  hideEmpty?: boolean
  hideToggle?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  isFetchingMore?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  inputClassName?: string
  id?: string
  name?: string
  maxLength?: number
}

const ComboboxCloseContext = React.createContext<(() => void) | null>(null)

export function ComboboxLinkSuffix({
  label,
  onClick,
}: {
  label: string
  onClick?: () => void
}) {
  const close = React.useContext(ComboboxCloseContext)
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        close?.()
        onClick?.()
      }}
      className="ml-auto animate-slide-down cursor-pointer text-2xs whitespace-nowrap text-gray-900 underline hover:text-primary"
    >
      {label}
    </button>
  )
}

export function ComboboxBadgeSuffix({
  label,
  onClick,
}: {
  label: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex animate-pop-in cursor-pointer items-center gap-1.25 rounded-sm bg-blue-100 px-1 text-2xs font-medium text-primary hover:bg-blue-100/80"
    >
      {label}
      <CloseIcon className="size-1.75 text-gray-500" />
    </button>
  )
}

const CONTROL_CLASS = 'h-8 gap-1.25 px-1.25'
const INPUT_TEXT_CLASS = 'text-sm'

function defaultFilter(option: ComboboxOption, query: string): boolean {
  if (!query) return true
  return option.label.toLowerCase().includes(query.toLowerCase())
}

function LabelRow({
  label,
  labelSize,
  suffix,
  htmlFor,
}: {
  label: string
  labelSize: LabelSize
  suffix?: React.ReactNode
  htmlFor: string
}) {
  return (
    <div className="flex w-full items-center gap-1.25">
      <Label htmlFor={htmlFor} size={labelSize}>
        {label}
      </Label>
      {suffix}
    </div>
  )
}

function useControlled<T>(
  controlled: T | undefined,
  defaultValue: T,
): [T, (next: T) => void] {
  const isControlled = controlled !== undefined
  const [internal, setInternal] = React.useState<T>(defaultValue)
  const value = isControlled ? (controlled as T) : internal
  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next)
    },
    [isControlled],
  )
  return [value, setValue]
}

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  dropdownRef: React.RefObject<HTMLElement | null>,
  handler: () => void,
  enabled: boolean,
) {
  React.useEffect(() => {
    if (!enabled) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (ref.current?.contains(target)) return
      if (dropdownRef.current?.contains(target)) return
      handler()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [ref, dropdownRef, handler, enabled])
}

const DROPDOWN_GAP_PX = 4
const VIEWPORT_MARGIN_PX = 8

type DropdownSide = 'top' | 'bottom'

function useAnchorRect(
  enabled: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
) {
  const [rect, setRect] = React.useState<DOMRect | null>(null)
  React.useLayoutEffect(() => {
    if (!enabled) return
    const update = () => {
      const el = anchorRef.current
      if (el) setRect(el.getBoundingClientRect())
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [enabled, anchorRef])
  return rect
}

function useDropdownSide(
  open: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  dropdownRef: React.RefObject<HTMLElement | null>,
  rect: DOMRect | null,
  optionCount: number,
): DropdownSide {
  const [side, setSide] = React.useState<DropdownSide>('bottom')
  React.useLayoutEffect(() => {
    if (!open) return
    const anchor = anchorRef.current
    const dropdown = dropdownRef.current
    if (!anchor || !dropdown) return
    const anchorRect = anchor.getBoundingClientRect()
    const height = dropdown.offsetHeight
    const spaceBelow =
      window.innerHeight - anchorRect.bottom - VIEWPORT_MARGIN_PX
    const spaceAbove = anchorRect.top - VIEWPORT_MARGIN_PX
    setSide(spaceBelow < height && spaceAbove > spaceBelow ? 'top' : 'bottom')
  }, [open, anchorRef, dropdownRef, rect, optionCount])
  return side
}

function dropdownStyle(
  rect: DOMRect | null,
  side: DropdownSide,
): React.CSSProperties {
  if (!rect)
    return { position: 'fixed', top: 0, left: 0, pointerEvents: 'none' }
  return {
    position: 'fixed',
    top:
      side === 'top'
        ? rect.top - DROPDOWN_GAP_PX
        : rect.bottom + DROPDOWN_GAP_PX,
    left: rect.left,
    minWidth: rect.width,
    transform: side === 'top' ? 'translateY(-100%)' : undefined,
    // z-80 (popout layer): above the modal layer (dialogs, z-70) and the detail
    // sidebar (z-60) so the body-portaled list isn't hidden when a combobox is
    // edited inside either. See the z-index scale in app/styles/index.css.
    zIndex: 80,
    // Explicitly interactive so the body-portaled list stays clickable and
    // scrollable inside a modal Radix Dialog (which sets body pointer-events
    // to none).
    pointerEvents: 'auto',
  }
}

type ComboboxState = {
  inputValue: string
  setInputValue: (v: string) => void
  open: boolean
  setOpen: (o: boolean) => void
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  filtered: ComboboxOption[]
  inputRef: React.RefObject<HTMLInputElement | null>
}

function useComboboxState({
  value,
  defaultValue,
  options,
  filter,
  onValueChange,
}: {
  value?: string
  defaultValue: string
  options: ComboboxOption[]
  filter: (o: ComboboxOption, q: string) => boolean
  onValueChange?: (v: string) => void
}): ComboboxState & { commit: (v: string) => void } {
  const [inputValue, setInputValueRaw] = useControlled(value, defaultValue)
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  const filtered = React.useMemo(
    () => options.filter((o) => filter(o, inputValue)),
    [options, inputValue, filter],
  )

  React.useEffect(() => {
    if (!open) setActiveIndex(-1)
  }, [open])

  React.useEffect(() => {
    if (activeIndex >= filtered.length) setActiveIndex(filtered.length - 1)
  }, [filtered.length, activeIndex])

  const commit = React.useCallback(
    (next: string) => {
      setInputValueRaw(next)
      onValueChange?.(next)
    },
    [setInputValueRaw, onValueChange],
  )

  return {
    inputValue,
    setInputValue: setInputValueRaw,
    open,
    setOpen,
    activeIndex,
    setActiveIndex,
    filtered,
    inputRef,
    commit,
  }
}

function moveActive(
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>,
  count: number,
  delta: number,
) {
  if (count === 0) return
  setActiveIndex((prev) => {
    const next = prev + delta
    if (next < 0) return count - 1
    if (next >= count) return 0
    return next
  })
}

const ARROW_DELTA: Partial<Record<string, number>> = {
  ArrowDown: 1,
  ArrowUp: -1,
}

function useKeyHandler({
  state,
  disabled,
  onEnter,
}: {
  state: ComboboxState
  disabled: boolean
  onEnter: (option: ComboboxOption) => void
}) {
  return (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    const { open, setOpen, filtered, activeIndex, setActiveIndex } = state
    const delta = ARROW_DELTA[e.key]
    if (delta !== undefined) {
      e.preventDefault()
      if (!open) setOpen(true)
      moveActive(setActiveIndex, filtered.length, delta)
      return
    }
    if (e.key === 'Enter' && open && activeIndex >= 0) {
      e.preventDefault()
      onEnter(filtered[activeIndex])
      return
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'Tab') setOpen(false)
  }
}

function OptionItem({
  option,
  index,
  listboxId,
  isActive,
  onHover,
  onSelect,
}: {
  option: ComboboxOption
  index: number
  listboxId: string
  isActive: boolean
  onHover: (i: number) => void
  onSelect: (option: ComboboxOption) => void
}) {
  return (
    <li
      id={`${listboxId}-opt-${index}`}
      role="option"
      aria-selected={isActive}
      aria-disabled={option.disabled}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={() => onHover(index)}
      onClick={() => onSelect(option)}
      className={cn(
        'flex w-full shrink-0 cursor-pointer items-center gap-2.5 rounded-sm px-2.5 py-1.25 text-sm text-gray-900',
        isActive && 'bg-gray-100',
        option.disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
      {option.tag && (
        <span className="flex shrink-0 items-center justify-center whitespace-nowrap rounded-sm bg-gray-100 px-1.25 py-0.5 text-sm text-gray-500">
          {option.tag}
        </span>
      )}
    </li>
  )
}

function ActionFooter({ action }: { action: ComboboxAction }) {
  return (
    <div className="flex flex-col">
      <div aria-hidden="true" className="h-px w-full shrink-0 bg-gray-200" />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={action.onClick}
        className="mt-1.25 flex min-h-8 w-full shrink-0 cursor-pointer items-center rounded-sm px-2.5 py-1 text-left text-sm text-primary hover:bg-blue-100"
      >
        + {action.label}
      </button>
    </div>
  )
}

const SKELETON_ROW_COUNT = 5

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <li key={i} className="flex h-8 shrink-0 items-center px-2.5">
          <Skeleton className="h-3 w-full" />
        </li>
      ))}
    </>
  )
}

function ComboboxLoadMoreSentinel({
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}) {
  const setNode = useIntersectionObserver({
    enabled: hasMore && !isFetchingMore,
    rootMargin: '32px',
    onIntersect: onLoadMore,
  })
  if (!hasMore && !isFetchingMore) return null
  return (
    <li
      ref={setNode}
      aria-hidden="true"
      className="flex h-8 shrink-0 items-center px-2.5"
    >
      {isFetchingMore && <Skeleton className="h-3 w-full" />}
    </li>
  )
}

interface OptionListProps {
  listboxId: string
  filtered: ComboboxOption[]
  activeIndex: number
  onHover: (i: number) => void
  onSelect: (option: ComboboxOption) => void
  emptyMessage?: React.ReactNode
  loading?: boolean
  action?: ComboboxAction
  onLoadMore?: () => void
  hasMore: boolean
  isFetchingMore: boolean
  dropdownRef: React.RefObject<HTMLDivElement | null>
  style: React.CSSProperties
}

function OptionList(props: OptionListProps) {
  const {
    listboxId,
    filtered,
    activeIndex,
    onHover,
    onSelect,
    emptyMessage,
    loading,
    action,
    onLoadMore,
    hasMore,
    isFetchingMore,
    dropdownRef,
    style,
  } = props
  const { t } = useTranslation()
  const hasOptions = filtered.length > 0

  React.useEffect(() => {
    const node = dropdownRef.current
    if (!node) return
    const scroller = node.querySelector<HTMLElement>('[role="listbox"]')
    const onWheel = (event: WheelEvent) => {
      event.stopPropagation()
      if (!scroller) return
      scroller.scrollTop += event.deltaY
      event.preventDefault()
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [dropdownRef])

  return (
    <div
      ref={dropdownRef}
      style={style}
      data-combobox-dropdown=""
      className="flex max-h-60 w-max max-w-[min(32rem,calc(100vw-1rem))] flex-col rounded-md bg-white p-1 shadow-popover"
    >
      <ul
        id={listboxId}
        role="listbox"
        className="flex min-h-0 flex-1 flex-col gap-1.25 overflow-auto overscroll-contain"
      >
        {loading && !hasOptions && <SkeletonRows />}
        {!loading && !hasOptions && (
          <li className="flex shrink-0 flex-col items-center gap-0.5 px-2.5 py-3 text-center">
            <span className="text-xs font-medium text-gray-900">
              {emptyMessage ?? t('combobox.noResults')}
            </span>
            <span className="text-2xs text-gray-500">
              {t('combobox.noResultsHint')}
            </span>
          </li>
        )}
        {hasOptions &&
          filtered.map((option, i) => (
            <OptionItem
              key={option.value}
              option={option}
              index={i}
              listboxId={listboxId}
              isActive={i === activeIndex}
              onHover={onHover}
              onSelect={onSelect}
            />
          ))}
        {onLoadMore && (
          <ComboboxLoadMoreSentinel
            hasMore={hasMore}
            isFetchingMore={isFetchingMore}
            onLoadMore={onLoadMore}
          />
        )}
      </ul>
      {action && <ActionFooter action={action} />}
    </div>
  )
}

function ComboboxInput({
  state,
  inputId,
  listboxId,
  placeholder,
  disabled,
  name,
  maxLength,
  onKeyDown,
  setInputRef,
  inputClassName,
}: {
  state: ComboboxState
  inputId: string
  listboxId: string
  placeholder?: string
  disabled: boolean
  name?: string
  maxLength?: number
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  setInputRef: (node: HTMLInputElement | null) => void
  inputClassName?: string
}) {
  const { inputValue, open, activeIndex, setOpen, commit } =
    state as ComboboxState & { commit: (v: string) => void }
  return (
    <input
      ref={setInputRef}
      id={inputId}
      name={name}
      type="text"
      role="combobox"
      autoComplete="off"
      aria-autocomplete="list"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-activedescendant={
        open && activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
      }
      value={inputValue}
      onChange={(e) => {
        commit(e.target.value)
        if (!open) setOpen(true)
      }}
      onFocus={() => setOpen(true)}
      onClick={() => setOpen(true)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className={cn(
        'h-full w-full min-w-0 flex-1 bg-transparent text-gray-900 placeholder:text-gray-300 focus-visible:outline-none disabled:cursor-not-allowed',
        INPUT_TEXT_CLASS,
        inputClassName,
      )}
    />
  )
}

function getControlClassName(
  error: boolean,
  success: boolean,
  disabled: boolean,
  className?: string,
) {
  return cn(
    'group/combobox relative flex w-full items-center rounded-sm border bg-white',
    CONTROL_CLASS,
    error
      ? 'border-red-500 focus-within:focus-ring-error'
      : success
        ? 'border-green-500 focus-within:focus-ring'
        : 'border-gray-200 focus-within:focus-ring',
    disabled && 'cursor-not-allowed opacity-50',
    className,
  )
}

function useForwardedRef<T>(
  forwarded: React.Ref<T>,
  inner: React.RefObject<T | null>,
) {
  return React.useCallback(
    (node: T | null) => {
      inner.current = node
      if (typeof forwarded === 'function') forwarded(node)
      else if (forwarded)
        (forwarded as React.MutableRefObject<T | null>).current = node
    },
    [forwarded, inner],
  )
}

function useComboboxHandlers({
  state,
  disabled,
  onSelect,
  onClear,
}: {
  state: ComboboxState & { commit: (v: string) => void }
  disabled: boolean
  onSelect?: (option: ComboboxOption) => void
  onClear?: () => void
}) {
  const selectOption = React.useCallback(
    (option: ComboboxOption) => {
      if (option.disabled) return
      state.commit(option.label)
      onSelect?.(option)
      state.setOpen(false)
      state.inputRef.current?.focus()
    },
    [state, onSelect],
  )

  const handleClear = React.useCallback(() => {
    state.commit('')
    onClear?.()
    state.setOpen(false)
  }, [state, onClear])

  const toggleOpen = React.useCallback(() => {
    if (disabled) return
    state.setOpen(!state.open)
    state.inputRef.current?.focus()
  }, [disabled, state])

  return { selectOption, handleClear, toggleOpen }
}

function ChevronToggle({
  open,
  onClick,
}: {
  open: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="flex shrink-0 cursor-pointer items-center justify-center px-0.75 py-2 text-gray-500"
      aria-label={t('combobox.toggleOptions')}
    >
      <ChevronDownIcon
        className={cn(
          'h-1.25 w-2.5 transition-transform',
          open && 'rotate-180',
        )}
      />
    </button>
  )
}

interface ControlRowProps {
  state: ComboboxState & { commit: (v: string) => void }
  inputId: string
  listboxId: string
  placeholder?: string
  disabled: boolean
  name?: string
  maxLength?: number
  error: boolean
  success: boolean
  className?: string
  inputClassName?: string
  showClear: boolean
  hideToggle?: boolean
  setInputRef: (node: HTMLInputElement | null) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onClear: () => void
  onToggle: () => void
}

function ControlRow(props: ControlRowProps) {
  const {
    state,
    inputId,
    listboxId,
    placeholder,
    disabled,
    name,
    maxLength,
    error,
    success,
    className,
    inputClassName,
    showClear,
    hideToggle,
    setInputRef,
    onKeyDown,
    onClear,
    onToggle,
  } = props
  return (
    <div
      data-slot="combobox-trigger"
      className={getControlClassName(error, success, disabled, className)}
    >
      <ComboboxInput
        state={state}
        inputId={inputId}
        listboxId={listboxId}
        placeholder={placeholder}
        disabled={disabled}
        name={name}
        maxLength={maxLength}
        onKeyDown={onKeyDown}
        setInputRef={setInputRef}
        inputClassName={inputClassName}
      />
      {!hideToggle && <ChevronToggle open={state.open} onClick={onToggle} />}
      {showClear && (
        <div
          data-slot="combobox-clear"
          className={cn(
            'pointer-events-none absolute top-1/2 -translate-y-1/2 bg-white opacity-0 transition-opacity duration-150',
            'group-hover/combobox:pointer-events-auto group-hover/combobox:opacity-100',
            'group-focus-within/combobox:pointer-events-auto group-focus-within/combobox:opacity-100',
            hideToggle ? 'right-1.25' : 'right-6.25',
          )}
        >
          <ClearButton onClick={onClear} />
        </div>
      )}
    </div>
  )
}

function useComboboxApi(
  props: ComboboxProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const state = useComboboxState({
    value: props.value,
    defaultValue: props.defaultValue ?? '',
    options: props.options,
    filter: props.filter ?? defaultFilter,
    onValueChange: props.onValueChange,
  })
  const setInputRef = useForwardedRef(ref, state.inputRef)
  useClickOutside(
    wrapperRef,
    dropdownRef,
    () => state.setOpen(false),
    state.open,
  )
  const handlers = useComboboxHandlers({
    state,
    disabled: !!props.disabled,
    onSelect: props.onSelect,
    onClear: props.onClear,
  })
  const onKeyDown = useKeyHandler({
    state,
    disabled: !!props.disabled,
    onEnter: handlers.selectOption,
  })
  return { wrapperRef, dropdownRef, state, setInputRef, handlers, onKeyDown }
}

function shouldRenderDropdown(
  props: ComboboxProps,
  filteredCount: number,
  open: boolean,
): boolean {
  if (!open) return false
  if (!props.hideEmpty) return true
  if (props.loading) return true
  if (filteredCount > 0) return true
  return false
}

function useNotifyComboboxOpen(
  open: boolean,
  onOpenChange?: (open: boolean) => void,
) {
  const ref = React.useRef(onOpenChange)
  React.useEffect(() => {
    ref.current = onOpenChange
  }, [onOpenChange])
  React.useEffect(() => {
    ref.current?.(open)
  }, [open])
}

function ComboboxImpl(props: ComboboxProps, ref: React.Ref<HTMLInputElement>) {
  const { label, labelSize = 'sm', suffix, id } = props
  const reactId = React.useId()
  const inputId = id ?? reactId
  const listboxId = `${inputId}-listbox`

  const { wrapperRef, dropdownRef, state, setInputRef, handlers, onKeyDown } =
    useComboboxApi(props, ref)
  const anchorRect = useAnchorRect(state.open, wrapperRef)
  const dropdownSide = useDropdownSide(
    state.open,
    wrapperRef,
    dropdownRef,
    anchorRect,
    state.filtered.length,
  )
  useNotifyComboboxOpen(state.open, props.onOpenChange)
  const showClear =
    (props.clearable ?? true) && !!state.inputValue && !props.disabled
  const renderDropdown = shouldRenderDropdown(
    props,
    state.filtered.length,
    state.open,
  )

  const closeDropdown = React.useCallback(() => state.setOpen(false), [state])

  return (
    <ComboboxCloseContext.Provider value={closeDropdown}>
      <div ref={wrapperRef} className="relative flex w-full flex-col gap-0.5">
        {label && (
          <LabelRow
            label={label}
            labelSize={labelSize}
            suffix={suffix}
            htmlFor={inputId}
          />
        )}
        <ControlRow
          state={state}
          inputId={inputId}
          listboxId={listboxId}
          placeholder={props.placeholder}
          disabled={!!props.disabled}
          name={props.name}
          maxLength={props.maxLength}
          error={!!props.error}
          success={!!props.success}
          className={props.className}
          inputClassName={props.inputClassName}
          showClear={showClear}
          hideToggle={props.hideToggle}
          setInputRef={setInputRef}
          onKeyDown={onKeyDown}
          onClear={handlers.handleClear}
          onToggle={handlers.toggleOpen}
        />
        {renderDropdown &&
          createPortal(
            <OptionList
              listboxId={listboxId}
              filtered={state.filtered}
              activeIndex={state.activeIndex}
              onHover={state.setActiveIndex}
              onSelect={handlers.selectOption}
              emptyMessage={props.emptyMessage}
              loading={props.loading}
              action={props.action}
              onLoadMore={props.onLoadMore}
              hasMore={!!props.hasMore}
              isFetchingMore={!!props.isFetchingMore}
              dropdownRef={dropdownRef}
              style={dropdownStyle(anchorRect, dropdownSide)}
            />,
            document.body,
          )}
      </div>
    </ComboboxCloseContext.Provider>
  )
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(
  ComboboxImpl,
)
